import { Workbook } from "exceljs";
import {
	DisbursedIncentiveRow,
	DisbursementVisit,
	DisbursedIncentivesFilters,
} from "../../services/reports.service";
import { resolveDisbursementResidual } from "../disbursementResidual";
import { applyHeaderStyle, currencyFmt, filenameSlug } from "./workbook-styles";
import { BuiltWorkbook } from "./build-workbook";
import { accountDateCell, excelDateFormat } from "../accountDate";

/** AC1 + C15537: the disbursements sheet. */
export const DISBURSEMENTS_SHEET = "Disbursements";

/** SIMS-8178 AC1: the per-visit sheet, named exactly as the AC names it. */
export const VISITS_SHEET = "Visits included";

export interface DisbursedIncentivesWorkbookInput
	extends DisbursedIncentivesFilters {
	rows: DisbursedIncentiveRow[];
	/** Whole-set total from the report — never re-summed from `rows`. */
	totalDisbursed: number;
	/** The heading the SCREEN rendered, e.g. "Member Full Name" — AC4. */
	beneficiaryHeading: string;
	/** The symbol the SCREEN rendered — AC4. */
	currencySymbol: string;
	/** Selected program names, for the filename — AC2 / C15809. */
	programNames?: string[];
	dateFormat?: string;
	/**
	 * SIMS-8178 — the linked visits, keyed by disbursement transaction id.
	 *
	 * Omitted (or empty) means Sheet 2 is not produced at all, which is the
	 * SIMS-8155 shape: a Disbursements-only workbook. Passing it in rather than
	 * fetching here keeps this builder pure and testable, and means Sheet 2 and
	 * the PDF visits section are built from ONE fetch rather than two.
	 */
	visitsByTransaction?: Record<
		string,
		{
			visits: DisbursementVisit[];
			earnedTotal: number;
			disbursedAmount: number;
		}
	>;
	/**
	 * The account's IANA timezone — SIMS-9013.
	 *
	 * Transaction Date is an INSTANT, so which calendar day it belongs to is a
	 * question only a timezone can answer. Left undefined, the export falls back
	 * to the exporting browser's zone, which is how this column came to disagree
	 * with both the screen and the report's own date filter.
	 */
	timeZone?: string;
}

/**
 * AC2 / C15809 — a multi-program export must not be labelled with one program's
 * name, and a single-program export should still say which program it was.
 */
export const buildDisbursedFilename = (
	programNames: string[] | undefined,
	extension: string,
): string => {
	const names = (programNames ?? []).filter(Boolean);
	const stem =
		names.length === 0
			? "disbursed-incentives"
			: names.length === 1
				? `disbursed-incentives-${filenameSlug(names[0])}`
				: `disbursed-incentives-${names.length}-programs`;
	return `${stem}.${extension}`;
};

/**
 * SIMS-8155 — the Disbursements export, built ENTIRELY IN THE BROWSER.
 *
 * Client-side for the same reason Visit Detail is: the backend export path
 * base64-encoded the workbook through API Gateway and it arrived undecoded, so
 * Excel rejected the file.
 *
 * **Two sheets, when there are visits to show.** SIMS-8155 shipped Sheet 1 alone
 * and named it "Disbursements" so that Epic SIMS-8177 could append without
 * restructuring; SIMS-8178 is that append. Sheet 2 appears only when
 * `visitsByTransaction` is supplied, so the SIMS-8155 shape — and C15537, which
 * asserts Sheet 2 is absent — still holds for a caller that does not pass it.
 *
 * **AC3 and AC4 hold by data path, not by re-implementation.** The rows arrive
 * from the same endpoint the screen used, so a withheld name already reads as an
 * em dash — this builder never sees the name and so cannot leak it. The term and
 * symbol are likewise the values the screen rendered, passed through. One value
 * read twice cannot diverge; two implementations kept in step eventually do.
 */
export const buildDisbursedIncentivesWorkbook = async (
	input: DisbursedIncentivesWorkbookInput,
): Promise<BuiltWorkbook> => {
	const workbook = new Workbook();
	const sheet = workbook.addWorksheet(DISBURSEMENTS_SHEET);

	const money = currencyFmt(input.currencySymbol);

	sheet.columns = [
		{ header: "Program", key: "programName", width: 28 },
		// SIMS-9013: a real DATE cell, formatted the way the screen formatted it.
		// It was a text cell holding an ISO string, so Excel sorted and filtered
		// the column lexicographically rather than chronologically.
		{
			header: "Transaction Date",
			key: "transactionDate",
			width: 18,
			style: { numFmt: excelDateFormat(input.dateFormat ?? "MM/DD/YYYY") },
		},
		{ header: "CIN", key: "cin", width: 16 },
		// AC4: the heading the screen showed, not a fixed word.
		{ header: input.beneficiaryHeading, key: "beneficiaryName", width: 28 },
		{ header: "Vendor Card", key: "vendorCard", width: 24 },
		{ header: "Delivery", key: "delivery", width: 16 },
		{ header: "Amount", key: "amount", width: 16, style: { numFmt: money } },
	];
	applyHeaderStyle(sheet.getRow(1));

	for (const row of input.rows) {
		sheet.addRow({
			programName: row.programName,
			// SIMS-9013: the calendar date IN THE ACCOUNT'S ZONE, as a real date
			// value. This used to be `transactionDate.slice(0, 10)` — the raw UTC
			// day — which put 52 of 95 rows a day later than the screen and the
			// PDF, because payouts are stored around the local midnight boundary
			// and every one of those falls into the next UTC day. Date-only still:
			// the file must not imply a precision the report does not show.
			transactionDate: accountDateCell(row.transactionDate, input.timeZone),
			cin: row.cin,
			// AC3: already an em dash when the account withholds names.
			beneficiaryName: row.beneficiaryName,
			vendorCard: row.vendorCard,
			delivery: row.delivery,
			amount: row.amount,
		});
	}

	// AC1: the grand total, taken from the report rather than re-summed from the
	// exported rows — those are capped, and a total that disagreed with the screen
	// is exactly the discrepancy this report exists to avoid.
	const totalRow = sheet.addRow({
		programName: "Total disbursed",
		amount: input.totalDisbursed,
	});
	totalRow.font = { bold: true };
	totalRow.getCell("amount").numFmt = money;

	// ---- Sheet 2: "Visits included" — SIMS-8178 -----------------------------
	//
	// AC1: each AFFECTED disbursement's FULL row repeated, with the linked
	// visit's six fields appended — one row per disbursement-and-linked-visit.
	// Repeating the disbursement columns rather than referencing Sheet 1 is what
	// makes this sheet usable on its own: a finance reader can pivot or filter it
	// without joining back, which is the whole reason DR-9 asks for it.
	const visitsByTransaction = input.visitsByTransaction;
	if (visitsByTransaction && Object.keys(visitsByTransaction).length > 0) {
		const visitSheet = workbook.addWorksheet(VISITS_SHEET);

		visitSheet.columns = [
			// The Sheet 1 columns, repeated.
			{ header: "Program", key: "programName", width: 28 },
			{
				header: "Transaction Date",
				key: "transactionDate",
				width: 18,
				style: { numFmt: excelDateFormat(input.dateFormat ?? "MM/DD/YYYY") },
			},
			{ header: "CIN", key: "cin", width: 16 },
			// AC5: the same heading Sheet 1 and the screen use — never a fixed word.
			{ header: input.beneficiaryHeading, key: "beneficiaryName", width: 28 },
			{ header: "Vendor Card", key: "vendorCard", width: 24 },
			{ header: "Delivery", key: "delivery", width: 16 },
			{
				header: "Amount",
				key: "amount",
				width: 16,
				style: { numFmt: money },
			},
			// The six visit fields DR-7 enumerates, in the order the panel draws
			// them, plus Activity — which the panel added because a linked entry is
			// not always a visit and without it a row reads as an amount beside
			// empty cells.
			{
				header: "Visit Date",
				key: "visitDate",
				width: 18,
				style: { numFmt: excelDateFormat(input.dateFormat ?? "MM/DD/YYYY") },
			},
			{ header: "Visit Number", key: "visitNumber", width: 14 },
			{ header: "Activity", key: "activity", width: 26 },
			{ header: "CM Coordinator", key: "cmCoordinator", width: 24 },
			{
				header: "Visit Earned Amount",
				key: "earnedAmount",
				width: 20,
				style: { numFmt: money },
			},
			{ header: "Visit Result", key: "visitResult", width: 16 },
			{ header: "Visit Payer Plan", key: "payerPlan", width: 24 },
		];
		applyHeaderStyle(visitSheet.getRow(1));

		for (const row of input.rows) {
			const entry = visitsByTransaction[row.transactionId];
			// A disbursement with no linked visits is not "affected" (AC1), so it
			// contributes no visit row. It is NOT dropped silently, though: if it
			// carries a residual, the line below still states it — otherwise a
			// payout tied to nothing would vanish from this sheet entirely, which
			// is precisely the case SIMS-8138 exists to surface.
			if (!entry) continue;

			// The disbursement half of every row on this sheet. AC4: the
			// beneficiary name arrives already masked from the same endpoint the
			// screen used, so a withheld name is an em dash before this code sees
			// it — repeated once per visit, it can still only be the em dash.
			const disbursementCells = {
				programName: row.programName,
				transactionDate: accountDateCell(row.transactionDate, input.timeZone),
				cin: row.cin,
				beneficiaryName: row.beneficiaryName,
				vendorCard: row.vendorCard,
				delivery: row.delivery,
				amount: row.amount,
			};

			for (const visit of entry.visits) {
				visitSheet.addRow({
					...disbursementCells,
					// SIMS-9013's rule applies to the visit date too: it is an instant,
					// and the day it belongs to is the account's day, not the exporting
					// browser's.
					visitDate: accountDateCell(visit.visitDate, input.timeZone),
					visitNumber: visit.visitNumber ?? "",
					activity: visit.activity || "",
					cmCoordinator: visit.cmCoordinator || "",
					earnedAmount: visit.earnedAmount,
					visitResult: visit.result || "",
					payerPlan: visit.payerPlan ?? "",
				});
			}

			// AC2 — the residual, carried through "the same way it appears on
			// screen": the SAME rule the drill-down uses, from the same module, so
			// the file and the panel cannot state different reconciliations of the
			// same payout. Written as its own row under the payout's visits, which
			// is where the panel puts it.
			const residual = resolveDisbursementResidual(
				entry.disbursedAmount,
				entry.earnedTotal,
			);
			if (residual) {
				const residualRow = visitSheet.addRow({
					...disbursementCells,
					activity: residual.label,
					earnedAmount: residual.amount,
				});
				residualRow.font = { italic: true };
			}
		}
	}

	const buffer = await workbook.xlsx.writeBuffer();
	return {
		blob: new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		}),
		filename: buildDisbursedFilename(input.programNames, "xlsx"),
	};
};
